import Box from '@mui/material/Box'
import { alpha } from '@mui/material/styles'
import type { FormNode } from '../../../../types/form.types'
import { ButtonElement, type ButtonElementProps } from './ButtonElement'
import { CommandBar as RuntimeCommandBar, type CommandItem } from '@/shared/components/CommandBar'
import { confirmAsync } from '@/shared/dialogs'
import {
  isListButtonDisabled,
  resolveListButtonClick,
} from '@/shared/FormRenderer/listFormActions'
import { resolvePinSlot, type PinSlot } from '@/shared/form/commandPin'

export type CommandBarRuntimeAction = 'submit' | 'close'

/**
 * Runtime commands the schema itself can't express (Print, Delete, ...).
 * Appended after the schema's own buttons so a form whose schema owns the
 * root CommandBar still ends up with exactly ONE bar — see RuntimeFormRenderer
 * (schemaHasRootCommandBar) for why these can't just ride the external shell bar too.
 */
export interface CommandBarExtras {
  secondary?: CommandItem[]
  overflow?: CommandItem[]
  onPin?: (id: string, slot: PinSlot) => void
  onResetPins?: () => void
  pinPrefs?: { pinned: string[]; overflow: string[] } | null
}

export interface CommandBarElementProps {
  node: FormNode
  preview?: boolean
  runtimeActions?: ButtonElementProps['runtimeActions']
  listActions?: ButtonElementProps['listActions']
  extraCommands?: CommandBarExtras
}

function schemaButtonDisabled(
  child: FormNode,
  runtimeActions: ButtonElementProps['runtimeActions'],
  listActions: ButtonElementProps['listActions'],
): boolean {
  const listHandler = listActions ? resolveListButtonClick(child, listActions) : undefined
  const dialogAction = runtimeActions?.resolveAction(child)
  const inRuntime = !!listActions || !!runtimeActions
  if (!inRuntime) return true
  if (listActions) return isListButtonDisabled(child, listActions) || !listHandler
  return dialogAction ? (runtimeActions!.disabled ?? false) : true
}

async function runSchemaButtonClick(
  child: FormNode,
  runtimeActions: ButtonElementProps['runtimeActions'],
  listActions: ButtonElementProps['listActions'],
): Promise<void> {
  if (child.confirmMessage && !(await confirmAsync(child.confirmMessage))) return
  if (listActions) {
    const listHandler = resolveListButtonClick(child, listActions)
    if (listHandler) {
      listHandler()
      return
    }
  }
  if (!runtimeActions) return
  const dialogAction = runtimeActions.resolveAction(child)
  if (!dialogAction) return
  if (dialogAction === 'submit') runtimeActions.submit()
  else if (dialogAction === 'save') runtimeActions.save?.()
  else runtimeActions.close()
}

export function CommandBarElement({ node, preview, runtimeActions, listActions, extraCommands }: CommandBarElementProps) {
  const children = node.children ?? []
  const footer = node.style?.commandBarVariant === 'footer'
  // Canvas-only affordance: mark the CommandBar as a visually distinct
  // accent zone. Never rendered in the real app (preview === true there).
  const isCanvasZone = !preview
  const pinPrefs = extraCommands?.pinPrefs ?? null
  const barButtons: FormNode[] = []
  const schemaOverflow: CommandItem[] = []
  for (const child of children) {
    if (child.type !== 'Button') continue
    if (preview && resolvePinSlot(child.name, child.pinDefault, pinPrefs) === 'overflow') {
      schemaOverflow.push({
        id: child.name,
        label: child.title || child.name,
        onClick: () => { void runSchemaButtonClick(child, runtimeActions, listActions) },
        disabled: schemaButtonDisabled(child, runtimeActions, listActions),
      })
    } else {
      barButtons.push(child)
    }
  }
  const mergedOverflow = [...schemaOverflow, ...(extraCommands?.overflow ?? [])]
  const hasRuntimeBar = !!(extraCommands?.secondary?.length || mergedOverflow.length)
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: footer ? 0 : 0.75,
        px: footer ? 0 : 1,
        borderBottom: footer ? 0 : 1,
        borderTop: footer && isCanvasZone ? 1 : 0,
        borderColor: isCanvasZone ? 'primary.main' : 'divider',
        bgcolor: footer
          ? 'transparent'
          : isCanvasZone
            ? (t) => alpha(t.palette.primary.main, 0.05)
            : 'background.paper',
        justifyContent: footer ? 'flex-end' : undefined,
        width: footer ? '100%' : undefined,
      }}
    >
      {barButtons.map((child) => (
        <ButtonElement
          key={child.id}
          node={child}
          runtimeActions={runtimeActions}
          listActions={listActions}
        />
      ))}
      {hasRuntimeBar && (
        <RuntimeCommandBar
          secondary={extraCommands?.secondary}
          overflow={mergedOverflow}
          onPin={extraCommands?.onPin}
          onResetPins={extraCommands?.onResetPins}
        />
      )}
    </Box>
  )
}
